/**
 * Music / SFX / CRT toggles, rendered as a small stack that `App` positions
 * over the bottom of the in-canvas scoreboard panel (kept aligned on resize).
 * Persisted per-browser by `App` (localStorage).
 */

export interface ToggleState {
  music: boolean;
  sfx: boolean;
  crt: boolean;
}

export type ToggleKey = keyof ToggleState;

const LABELS: Record<ToggleKey, string> = {
  music: "♪ Music",
  sfx: "♫ Sound",
  crt: "CRT",
};

const ORDER: readonly ToggleKey[] = ["music", "sfx", "crt"];

export class Toggles {
  /** Fired when the user clicks a toggle (not when `set` is called externally). */
  onChange: (key: ToggleKey, value: boolean) => void = () => {};

  private readonly state: ToggleState;
  private readonly root: HTMLElement;

  constructor(mount: HTMLElement, initial: ToggleState) {
    this.state = { ...initial };
    this.root = document.createElement("div");
    this.root.className = "toggles";
    mount.appendChild(this.root);
    this.root.addEventListener("click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-toggle]");
      if (!el) return;
      const key = el.dataset.toggle as ToggleKey;
      this.state[key] = !this.state[key];
      this.render();
      this.onChange(key, this.state[key]);
    });
    this.render();
  }

  get(): Readonly<ToggleState> {
    return this.state;
  }

  /** Reflect an external change without firing `onChange`. */
  set(key: ToggleKey, value: boolean): void {
    if (this.state[key] === value) return;
    this.state[key] = value;
    this.render();
  }

  /** Align the stack to a rectangle (CSS pixels, viewport-relative). */
  place(x: number, y: number, width: number): void {
    const s = this.root.style;
    s.left = `${x}px`;
    s.top = `${y}px`;
    s.width = `${width}px`;
  }

  private render(): void {
    this.root.innerHTML = ORDER.map(
      (k) =>
        `<button type="button" data-toggle="${k}" aria-pressed="${this.state[k]}" ` +
        `class="toggle ${this.state[k] ? "on" : ""}">${LABELS[k]}</button>`,
    ).join("");
  }
}
